import { Button } from "@/components/ui/baseUi/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/baseUi/dialog"
import { Text } from "@/components/ui/baseUi/text"
import { NewChatGroupNameForm } from "@/modules/chat/components/newChatGroupNameForm"
import type { GetStreamUserEntity } from "@/modules/chat/entities/getStreamUserEntity"
import { LoadingSpinner } from "@/modules/ui/components/loading"
import { UserSearchList } from "@modules/chat/components/userSearchList"
import { UserSearchSelection } from "@modules/chat/components/userSearchSelection"
import { useChatPageContext } from "@modules/chat/contexts/chatPageContext"
import { useCreateChannel } from "@modules/chat/hooks/useCreateChat"
import { CHANNEL_LIST_SHOWN_WINDOW_WIDTH } from "@modules/chat/utils/chatConstants"
import { word } from "@modules/core/utils/i18n"
import useDebounce from "@modules/hooks/useDebounce"
import { ListHeader } from "@modules/ui/components/header"
import { useWindowSize } from "@modules/utils/hooks/useWindowsSize"
import { CheckIcon, MenuIcon } from "lucide-react"
import { Suspense, useState } from "react"

export function NewChatChannelForm() {
  const {
    selectedUsers,
    clearSelectedUsers,
    toggleUser,
    isCreatingGroup,
    updateIsCreatingGroup,
    removeUser,
    updateIsCreatingNewChannel,
    toggleDisplayChannelList,
    isAddingMembersToChannel,
    updateIsAddingMembersToChannel,
  } = useChatPageContext()

  const [displayGroupNameInput, updateDisplayGroupNameInput] = useState<boolean>(false)
  const [searchTerm, setSearchTerm] = useState<string>("")

  const { createOneToOneChannel, createGroupChannel } = useCreateChannel()
  const { width } = useWindowSize()
  const debouncedSearchTerm = useDebounce(searchTerm, 300)

  const onUserClick = async (userId: string) => {
    if (isAddingMembersToChannel) {
      await isAddingMembersToChannel.addMembers([userId])
      updateIsAddingMembersToChannel(undefined)
      clearSelectedUsers()
    } else await onCreateOneToOneChannel(userId)
  }

  const onCreateOneToOneChannel = async (userId: string) => {
    await createOneToOneChannel(userId)
    clearSelectedUsers()
    updateIsCreatingNewChannel(false)
    updateIsCreatingGroup(false)
  }

  const validateGroupCreation = async (groupName: string) => {
    await createGroupChannel(
      groupName,
      selectedUsers.map((u) => u.id),
    )
    clearSelectedUsers()
    updateIsCreatingNewChannel(false)
    updateIsCreatingGroup(false)
  }

  return (
    <div className="str-chat flex w-full min-w-[70%] flex-col rounded-tr-xl bg-surface-background">
      <div className="mb-1 flex flex-row items-center justify-between p-2 pl-3">
        {width <= CHANNEL_LIST_SHOWN_WINDOW_WIDTH && (
          <Button variant="outline" size="iconMd" onClick={toggleDisplayChannelList}>
            <MenuIcon className="size-4" />
          </Button>
        )}

        <ListHeader
          title={isCreatingGroup ? word("chat.create_group.title") : word("chat.search.title")}
          className="flex-1 justify-between"
          search={{
            onChange: (e) => setSearchTerm(e.target.value),
            placeholder: word("chat.search.placeholder"),
            searchTerm,
          }}
        >
          {isCreatingGroup && (
            <Button
              variant="primary"
              onClick={() => updateDisplayGroupNameInput(true)}
              disabled={!(selectedUsers.length > 0)}
            >
              <CheckIcon className="size-4" />
              {width > 1440 ? word("global.check") : ""}
            </Button>
          )}
        </ListHeader>
      </div>
      {selectedUsers.length > 0 && <UserSearchSelection users={selectedUsers} onRemoveUser={removeUser} />}

      <Suspense fallback={<LoadingSpinner />}>
        <UserSearchList
          isCreatingGroup={isCreatingGroup}
          selectedUsers={selectedUsers}
          onUserSelect={(user: GetStreamUserEntity) => toggleUser(user)}
          onUserClick={(userId: string) => {
            if (isCreatingGroup) return
            void onUserClick(userId)
          }}
          searchTerm={debouncedSearchTerm ?? ""}
        />
      </Suspense>

      <Dialog open={displayGroupNameInput} onOpenChange={() => updateDisplayGroupNameInput(false)}>
        <DialogContent className="w-125">
          <DialogHeader>
            <DialogTitle>
              <Text fontStyle="TL" className="flex w-full pb-4 pl-4">
                {word("chat.add_group_name")}
              </Text>
            </DialogTitle>
          </DialogHeader>
          <NewChatGroupNameForm
            onValidate={(groupName: string) => {
              void validateGroupCreation(groupName)
            }}
            onCancel={() => {
              void updateDisplayGroupNameInput(false)
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  )
}
